import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigationType, useNavigate } from "react-router-dom";
import { Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { getProducts } from "@/lib/api";
import { useStore } from "@/lib/store";
import ProductCard from "@/components/ProductCard";
import SizzhkaLoader from "@/components/SizzhkaLoader";

const PAGE_SIZES = [10, 20, 30, 40];
const SCROLL_KEY = "catalog_scroll_state";

// Disable browser's built-in scroll restoration — we handle it manually
if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

export default function CatalogPage() {
  const { category: urlCategory } = useParams();
  const location = useLocation();
  const navType = useNavigationType();
  
  // For POP navigation (browser back), restore from sessionStorage
  const savedState = useRef((() => {
    if (navType !== "POP") return null;
    try { return JSON.parse(sessionStorage.getItem(SCROLL_KEY)); } catch { return null; }
  })());
  const restoringRef = useRef(savedState.current !== null);

  const [category, setCategory] = useState(urlCategory || "all");
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem("pageSize");
    return saved ? Number(saved) : 20;
  });

  // Restore page number from sessionStorage if returning via POP
  const [currentPage, setCurrentPage] = useState(() => {
    if (savedState.current?.page) return savedState.current.page;
    return 1;
  });

  const { t, categories, getCategoryLabel, language } = useStore();
  const hasRestoredScroll = useRef(false);
  const nav = useNavigate();

  // Change category and update URL
  const changeCategory = useCallback((slug) => {
    setCategory(slug);
    const newPath = slug === "all" ? "/catalog" : `/catalog/${slug}`;
    nav(newPath, { replace: true });
  }, [nav]);

  useEffect(() => {
    localStorage.setItem("pageSize", String(pageSize));
  }, [pageSize]);

  // Save scroll position when clicking a product link
  useEffect(() => {
    const handleProductClick = (e) => {
      const link = e.target.closest('a[href*="/product/"]');
      if (link) {
        sessionStorage.setItem(SCROLL_KEY, JSON.stringify({
          scrollY: window.scrollY,
          page: currentPage,
        }));
      }
    };
    
    document.addEventListener("click", handleProductClick, { capture: true });
    return () => document.removeEventListener("click", handleProductClick, { capture: true });
  }, [currentPage]);

  useEffect(() => {
    if (urlCategory) setCategory(urlCategory);
  }, [urlCategory]);

  useEffect(() => {
    // Skip page reset on initial mount when restoring scroll
    if (restoringRef.current) {
      restoringRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [category, search, statusFilter, pageSize]);

  useEffect(() => {
    setLoading(true);
    const skip = (currentPage - 1) * pageSize;
    const params = { limit: pageSize, skip };
    if (category !== "all") params.category = category;
    if (search.trim()) params.search = search.trim();
    if (statusFilter) params.status = statusFilter;
    getProducts(params)
      .then((data) => {
        setProducts(data.products || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [category, search, statusFilter, pageSize, currentPage]);

  // Restore scroll position after products are loaded
  useEffect(() => {
    const targetY = savedState.current?.scrollY;
    if (!targetY || targetY <= 0 || hasRestoredScroll.current || loading || products.length === 0) return;
    hasRestoredScroll.current = true;

    // Custom smooth scroll with easeOutCubic — feels natural, no jank
    const duration = Math.min(600, 300 + Math.abs(targetY) * 0.15); // adaptive: longer for bigger distance
    const startY = window.scrollY;
    const diff = targetY - startY;
    let startTime = null;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);

      window.scrollTo(0, startY + diff * eased);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, [loading, products]);

  // Scroll to top of product list when pagination page changes (but NOT on initial restore)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Scroll to the products grid area smoothly
    const grid = document.querySelector('[data-testid="products-grid"]');
    if (grid) {
      const top = grid.getBoundingClientRect().top + window.scrollY - 60;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentPage]);

  const totalPages = Math.ceil(total / pageSize);

  const getPageNumbers = () => {
    const pages = [];
    const maxShow = 5;
    let start = Math.max(1, currentPage - Math.floor(maxShow / 2));
    let end = Math.min(totalPages, start + maxShow - 1);
    if (end - start + 1 < maxShow) start = Math.max(1, end - maxShow + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // Scroll refs and state for category tabs
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll, categories]);

  const scrollCategories = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 200, behavior: "smooth" });
  };

  // Build category tabs from dynamic data
  const catTabs = [{ slug: "all", label: t("all") }, ...categories.map(c => ({ slug: c.slug, label: getCategoryLabel(c.slug) }))];

  const statusLabels = language === "EN" 
    ? { all: "All", "in-stock": "In stock", "pre-order": "Pre-order" }
    : { all: "Все", "in-stock": "В наличии", "pre-order": "Под заказ" };
  
  const searchPlaceholder = language === "EN" ? "Search by title..." : "Поиск по названию...";
  const nothingFound = language === "EN" ? "Nothing found" : "Ничего не найдено";
  const itemsLabel = language === "EN" ? "items" : "шт.";
  const perPage = language === "EN" ? "/ page" : "/ стр.";

  return (
    <div className="animate-fade-in" data-testid="catalog-page">
      {/* Search Bar */}
      <div className="border-b border-zinc-800 px-4 py-3">
        {searchOpen ? (
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-zinc-500 shrink-0" />
            <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none"
              placeholder={searchPlaceholder} data-testid="search-input" />
            <button onClick={() => { setSearchOpen(false); setSearch(""); }} className="p-1" data-testid="search-close">
              <X className="w-4 h-4 text-zinc-500" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg tracking-wider">{t("catalog")}</h2>
            <button onClick={() => setSearchOpen(true)} className="p-2 hover:bg-zinc-900 transition-colors" data-testid="search-toggle">
              <Search className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Category Tabs with scroll arrows */}
      <div className="border-b border-zinc-800 relative group/cats">
        {/* Left arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scrollCategories(-1)}
            className="absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-black via-black/90 to-transparent flex items-center justify-center hover:w-10 transition-all"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-4 h-4 text-zinc-400" />
          </button>
        )}
        {/* Right arrow */}
        {canScrollRight && (
          <button
            onClick={() => scrollCategories(1)}
            className="absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-black via-black/90 to-transparent flex items-center justify-center hover:w-10 transition-all"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-4 h-4 text-zinc-400" />
          </button>
        )}
        <div ref={scrollRef} className="overflow-x-auto scrollbar-hide">
          <div className="flex min-w-max">
            {catTabs.map((cat) => (
              <button key={cat.slug} onClick={() => changeCategory(cat.slug)}
                className={`px-4 py-3 text-xs font-display tracking-wider whitespace-nowrap transition-colors border-b-2 ${
                  category === cat.slug ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid={`cat-tab-${cat.slug}`}>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Status Filter + Page Size */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-800/50 flex-wrap">
        {[
          { val: "", label: statusLabels.all },
          { val: "in-stock", label: statusLabels["in-stock"] },
          { val: "pre-order", label: statusLabels["pre-order"] },
        ].map((f) => (
          <button key={f.val} onClick={() => setStatusFilter(f.val)}
            className={`text-[10px] font-mono uppercase px-2 py-1 border transition-colors ${
              statusFilter === f.val ? "bg-white text-black border-white" : "border-zinc-700 text-zinc-500 hover:border-zinc-500"
            }`}
            data-testid={`status-filter-${f.val || "all"}`}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-mono text-zinc-600">{total} {itemsLabel}</span>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-zinc-900 border border-zinc-800 text-[10px] font-mono text-zinc-400 px-2 py-1 focus:outline-none"
            data-testid="page-size-select">
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>{s} {perPage}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <SizzhkaLoader />
      ) : products.length === 0 ? (
        <div className="flex items-center justify-center h-[40vh] text-zinc-500 text-sm">{nothingFound}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 border-x border-zinc-800" data-testid="products-grid">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 py-4 px-4" data-testid="pagination">
          <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
            className="w-8 h-8 border border-zinc-800 flex items-center justify-center hover:bg-zinc-900 transition-colors disabled:opacity-30"
            data-testid="page-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          {getPageNumbers().map((page) => (
            <button key={page} onClick={() => setCurrentPage(page)}
              className={`w-8 h-8 border text-xs font-mono transition-colors ${
                page === currentPage ? "bg-white text-black border-white" : "border-zinc-800 text-zinc-500 hover:bg-zinc-900"
              }`}
              data-testid={`page-${page}`}>
              {page}
            </button>
          ))}
          <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            className="w-8 h-8 border border-zinc-800 flex items-center justify-center hover:bg-zinc-900 transition-colors disabled:opacity-30"
            data-testid="page-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}